import React from 'react'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import { getParsedDate } from 'utils/dateParser'

import { ChatLabelKey } from './historyLabels'
import { TOpenedChat } from './types'

type TFilters = Record<string, string[]>

type TFilterChip = {
  attribute: string
  value: string
  // in the table's view but not the chat's
  missing: boolean
}

function valuesNotIn(filters: TFilters, other: TFilters) {
  return Object.entries(filters).flatMap(([attribute, values]) =>
    values
      .filter((value) => !(other[attribute] ?? []).includes(value))
      .map((value) => ({ attribute, value }))
  )
}

function filterChips(
  chatFilters: TFilters,
  viewFilters: TFilters
): TFilterChip[] {
  return [
    ...valuesNotIn(chatFilters, viewFilters).map((chip) => ({
      ...chip,
      missing: false
    })),
    ...valuesNotIn(viewFilters, chatFilters).map((chip) => ({
      ...chip,
      missing: true
    }))
  ]
}

interface ResumedChatHeaderProps {
  // Null until the first reply names the chat.
  chat: TOpenedChat | null
  onBack: () => void
  onAskAgain: () => void
  viewFilters: TFilters
}

const ResumedChatHeader = (props: ResumedChatHeaderProps) => {
  const { chat, onBack, onAskAgain, viewFilters } = props

  const { lang, t } = useTranslation()
  const translate = (key: ChatLabelKey, values?: Record<string, string>) =>
    t('chat', key, false, values)

  const subtitle = [
    chat?.created_at ? getParsedDate(chat.created_at, lang, { time: 'full' }) : null,
    chat && chat.row_count !== null
      ? translate('history_row_count', { count: String(chat.row_count) })
      : null
  ]
    .filter(Boolean)
    .join(' · ')

  const chips = chat?.filters ? filterChips(chat.filters, viewFilters) : []
  const hasMissing = chips.some((chip) => chip.missing)

  return (
    <Box sx={{ flex: '0 0 auto' }}>
      <Box sx={{ px: 2, pt: 1 }}>
        <Text
          color="text.secondary"
          onClick={onBack}
          variant="body2"
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'inline-flex',
            gap: 1,
            '&:hover': { color: 'text.primary' }
          }}
        >
          <Icon fontSize="small" name="ArrowBack" />
          {t('chat', 'history_title')}
        </Text>

        {chat?.title ? (
          <Text color="text.primary" variant="h5" sx={{ mt: 1 }}>
            {chat.title}
          </Text>
        ) : null}

        {subtitle ? (
          <Text color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
            {subtitle}
          </Text>
        ) : null}

        {chips.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Text color="text.secondary" variant="caption">
              {t('chat', 'resumed_chat_other_view')}
              {hasMissing ? ` ${t('chat', 'resumed_chat_missing_filters')}` : null}
            </Text>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
            >
              {chips.map((chip) => (
                <Chip
                  color="primary"
                  key={`${chip.attribute}-${chip.value}`}
                  label={chip.value}
                  size="xsmall"
                  sx={
                    chip.missing
                      ? { textDecoration: 'line-through' }
                      : undefined
                  }
                  variant={chip.missing ? 'outlined' : 'filled'}
                />
              ))}
            </Box>
          </Box>
        ) : null}

        {chat?.read_only ? (
          <Box
            sx={{
              bgcolor: 'brand.transparent',
              border: '1px solid',
              borderColor: 'brand.light',
              borderRadius: 1,
              display: 'flex',
              gap: 1,
              mt: 1.5,
              p: 1.5
            }}
          >
            <Icon
              fontSize="small"
              name="RefreshOutlined"
              sx={{ flex: '0 0 auto', mt: 0.25 }}
            />
            <Box>
              <Text color="text.primary" variant="body2">
                {t('chat', 'resumed_chat_stale')}
              </Text>
              <Button
                onClick={onAskAgain}
                size="small"
                sx={{ mt: 0.5, px: 0 }}
                variant="text"
              >
                {t('chat', 'resumed_chat_ask_again')}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ mt: 1.5 }} />
    </Box>
  )
}

export default ResumedChatHeader
